import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

const PORTFOLIO_SITES = [
  { name: "Bimini Hydrotherapy", url: "biminihydrotherapy.com", industry: "Health & Wellness", slug: "bimini-hydrotherapy-aeo-geo-visibility", img: "/screenshots/biminihydrotherapy.jpg" },
  { name: "NinjaAthlete", url: "ninjathlete.com", industry: "Fitness & Athletics", slug: "ninja-athlete-llm-visibility-fitness", img: "/screenshots/ninjathlete.jpg" },
  { name: "DFW Apartments", url: "dfwapartmentlocators.com", industry: "Real Estate", slug: "dfw-apartments-ai-search-real-estate", img: "/screenshots/dfwapartments.jpg" },
  { name: "Dallas Luxury Realty", url: "dallasluxuryrealty.com", industry: "Real Estate", slug: "dallas-luxury-realty-aeo-real-estate", img: "/screenshots/dallasluxuryrealty.jpg" },
  { name: "USA Ink Printing", url: "usainkprinting.com", industry: "Printing & E-Commerce", slug: "usa-ink-printing-aeo-ecommerce", img: "/screenshots/usainkprinting.jpg" },
  { name: "Ninja Screen Print", url: "ninjascreenprint.com", industry: "Printing Services", slug: "ninja-screen-print-geo-local-seo", img: "/screenshots/ninjascreenprint.jpg" },
  { name: "Gray Contracting DFW", url: "graycontractingdfw.com", industry: "Home Services", slug: "gray-contracting-dfw-contractor-aeo", img: "/screenshots/graycontractingdfw.jpg" },
  { name: "Dallas Haul Stars", url: "dallashaulstars.com", industry: "Home Services", slug: "dallas-haul-stars-junk-removal-aeo", img: "/screenshots/dallashaulstars.jpg" },
  { name: "Golden Oak Odessa", url: "goldenoakodessa.com", industry: "Senior Living", slug: "golden-oak-odessa-senior-living-aeo", img: "/screenshots/goldenoakodessa.jpg" },
  { name: "TruLink Gutters", url: "trulinkgutters.com", industry: "Home Services", slug: "trulink-gutters-home-services-aeo", img: "/screenshots/trulinkgutters.jpg" },
  { name: "TrainAgents", url: "trainagents.com", industry: "AI & Technology", slug: "train-agents-ai-training-platform-aeo", img: "/screenshots/trainagents.jpg" },
];

const PortfolioSection = () => {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <section className="py-20 relative overflow-hidden" data-testid="portfolio-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#00E676 1px, transparent 1px), linear-gradient(90deg, #00E676 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00E676]/20 bg-[#00E676]/5 mb-5">
            <Globe className="w-4 h-4 text-[#00E676]" />
            <span className="text-sm text-[#00E676] font-medium">Our Portfolio</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Websites Created by <span className="text-[#00E676]">Digital Media Ninja</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base leading-relaxed">
            Every site is built by our <Link to="/services/web-design" className="text-[#00E676] hover:underline underline-offset-2 font-medium">web design</Link> team and optimized for AEO, GEO, and LLM visibility from day one — so AI assistants like ChatGPT, Perplexity, and Google recommend our clients.
          </p>
        </div>

        {/* Sites grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PORTFOLIO_SITES.map((site, i) => (
            <div
              key={site.url}
              className="group relative bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden hover:border-[#00E676]/25 transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,230,118,0.06)]"
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
              data-testid={`portfolio-site-${i}`}
            >
              {/* Thumbnail */}
              <a href={`https://${site.url}`} target="_blank" rel="noopener noreferrer" className="block relative aspect-[16/10] overflow-hidden bg-gray-900">
                <img
                  src={site.img}
                  alt={`${site.name} website`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10 pointer-events-none" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="text-white text-xs font-medium flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                    <ExternalLink className="w-3.5 h-3.5" /> Visit Site
                  </span>
                </div>
              </a>

              {/* Card body */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-white font-semibold text-[15px] group-hover:text-[#00E676] transition-colors">
                      {site.name}
                    </h3>
                    <span className="text-[11px] text-gray-600 uppercase tracking-wider font-medium">{site.industry}</span>
                  </div>
                  <a
                    href={`https://${site.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-lg bg-white/5 text-gray-500 hover:text-[#00E676] hover:bg-[#00E676]/10 transition-colors"
                    aria-label={`Visit ${site.name}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>

                <p className="text-gray-500 text-xs font-mono mb-3">{site.url}</p>

                {/* AI Visibility badges */}
                <div className="flex items-center gap-1.5 mb-3">
                  {["ChatGPT", "Perplexity", "AI Overviews"].map((badge) => (
                    <span key={badge} className="px-2 py-0.5 rounded-full bg-[#00E676]/[0.07] text-[#00E676]/80 text-[10px] font-medium border border-[#00E676]/10">
                      {badge}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/case-studies/${site.slug}`}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#00E676] transition-colors font-medium"
                >
                  View case study <ArrowRight className={`w-3 h-3 transition-transform ${hoveredIdx === i ? "translate-x-0.5" : ""}`} />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm mb-5">
            Want a website that ranks on Google <em>and</em> gets recommended by AI?
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/services/web-design">
              <Button className="btn-ninja px-8">
                Get a Website Like These
              </Button>
            </Link>
            <Link to="/case-studies">
              <Button variant="outline" className="btn-outline">
                View All Case Studies <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
